import { useEffect, useState, type ReactNode } from 'react';
import { ApiError, get, post, type StoryDto } from '../api.js';
import { StoryStatusBadge } from '../status-badge.js';
import { currentLocale, t } from '../i18n.js';
import { Link, navigate, useLocation } from '../router.js';
import { fmtDateInput, fmtDateTime, mmss } from '../util.js';
import { Dialog, previewOf } from './rundown-dialogs.js';
import { CgPreview, PreviewDialog, templateName, type CueDto } from './storyboard-panel.js';
import { strapDesignFor } from '@airpost/shared';
import { StrapDesignContext } from '../strap-view.js';

interface Loaded {
  story: StoryDto;
  createdBy: string | null;
  updatedBy: string | null;
  originName: string | null;
  canEdit: boolean;
  placements: Array<{ rundownId: string; itemId: string; date: string; programmeName: string; position: number }>;
}
interface Revision {
  revision: number;
  action: string;
  title: string;
  savedAt: string;
  savedBy: string | null;
  clientIp: string | null;
  bodyText: string;
  bodyHtml: string;
}

/** The clip itself (its edit when the story plays one, as the preview does) in a small player, the thumbnail as its poster; a note when the playout has no preview. */
function ClipInline({ mediaAssetId, cue }: { mediaAssetId: string; cue?: CueDto }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <span className="muted small">{t('cueNoPreview')}</span>;
  const { src, note } = previewOf(mediaAssetId, cue?.edit, cue);
  return <video className="sv-clip" controls preload="none" playsInline poster={`/api/media/${encodeURIComponent(mediaAssetId)}/thumb`} src={src} title={note || undefined} onError={() => setFailed(true)} />;
}

export function StoryViewPage({ id }: { id: string }) {
  const [data, setData] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<Revision[] | null>(null);
  const [selectedRev, setSelectedRev] = useState<Revision | null>(null);
  const [categories, setCategories] = useState<Record<string, string>>({});
  // the storyboard: the video (clip or virtual code) and the CG titles of the story, in playing order
  const [cues, setCues] = useState<CueDto[] | null>(null);
  const [preview, setPreview] = useState<CueDto | null>(null);
  // the rundown the user came from, so Exit lands on the same row
  const from = useLocation().query.get('from');

  const load = () =>
    get<Loaded>(`/api/stories/${id}`)
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  useEffect(() => {
    void load();
    get<{ categories: Array<{ id: string; name: string }> }>('/api/categories').then((r) => setCategories(Object.fromEntries(r.categories.map((c) => [c.id, c.name]))));
    get<{ cues: CueDto[] }>(`/api/stories/${id}/storyboard`)
      .then((r) => setCues(r.cues))
      .catch(() => setCues([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openHistory = async () => {
    const r = await get<{ revisions: Revision[] }>(`/api/stories/${id}/revisions`);
    setHistory(r.revisions);
    setSelectedRev(r.revisions[0] ?? null);
  };
  const restore = async (rev: number) => {
    if (!window.confirm(`Επαναφορά της αναθεώρησης ${rev}; Δημιουργείται νέα αναθεώρηση.`)) return;
    await post(`/api/stories/${id}/restore`, { revision: rev });
    setHistory(null);
    await load();
  };

  if (error) return <div className="banner err">{error}</div>;
  if (!data) return <div>{t('loading')}</div>;
  const { story } = data;
  const first = data.placements[0];
  const place = data.placements.find((p) => p.rundownId === from) ?? first;
  const exitTo = place ? `/rundown/${place.rundownId}?item=${place.itemId}` : null;
  const locale = currentLocale();
  const actions = (
    <div className="sv-actions">
      {exitTo && (
        <button className="btn" onClick={() => navigate(exitTo)} title={t('svExitHint')}>
          ⏏ {t('svExit')}
        </button>
      )}
      <button className="btn" onClick={() => window.history.back()}>
        ← {t('back')}
      </button>
      {first && (
        <a className="btn" href={`/api/rundowns/${first.rundownId}/exports/pdf-full?items=${first.itemId}`} target="_blank" rel="noreferrer">
          📄 {t('pdf')}
        </a>
      )}
      <button className="btn" onClick={() => void openHistory()}>
        🕘 {t('history')}
      </button>
      <a className="btn sv-web" href={`/web/${id}`} target="_blank" rel="noreferrer" title={t('svWebHint')}>
        <img src="/brand/alphanews-logo.png" alt="alphanews.live" width={522} height={76} />
      </a>
      {data.canEdit && (
        <button className="btn primary" onClick={() => navigate(`/story/${id}/edit${from ? `?from=${from}` : ''}`)}>
          ✎ {t('storyEdit')}
        </button>
      )}
    </div>
  );
  const readS = story.readOverrideS ?? story.readComputedS;
  const secs = (ms: number) => mmss(Math.round(ms / 1000));
  const firstVideo = cues?.find((c) => c.kind === 'video') ?? null;
  const videoPanel = !cues ? (
    <div className="sv-stage empty">{t('loading')}</div>
  ) : firstVideo?.media ? (
    <>
      <ClipInline mediaAssetId={firstVideo.mediaAssetId ?? firstVideo.media.id} cue={firstVideo} />
      <div className="sv-video-info">
        <div className="sv-video-text">
          <b>{firstVideo.edit ? `✂ ${firstVideo.edit.title}` : firstVideo.media.title}</b>
          <span className="muted">
            {firstVideo.media.filename} · {secs(firstVideo.media.durationMs)} · {firstVideo.media.status}
          </span>
        </div>
        <button className="btn" type="button" onClick={() => setPreview(firstVideo)}>
          ⤢ {t('cuePreview')}
        </button>
      </div>
    </>
  ) : firstVideo?.mediaCode || story.mediaCode ? (
    <div className="sv-stage waiting">
      <b>{firstVideo?.mediaCode ?? story.mediaCode}</b>
      <span>{t('virtualWaiting')}</span>
    </div>
  ) : (
    <div className="sv-stage none">{t('storyVideoNone')}</div>
  );
  const board = !cues ? null : cues.length === 0 ? (
    <p className="sv-empty">{t('svNoCues')}</p>
  ) : (
    <ol className="sv-cues">
      {cues.map((c, i) => {
        // a title's first line names it; one with no text yet is named by its template
        const lines = c.lines.filter((l) => l.trim());
        return (
        <li key={c.id} className={`sv-cue ${c.kind === 'video' ? 'video' : 'cg'}`}>
          <span className="sv-cue-n">{i + 1}</span>
          {c.kind === 'video' ? (
            c.mediaAssetId ? (
              <button type="button" className="sv-thumb" title={`▶ ${t('cuePreview')}`} onClick={() => setPreview(c)}>
                <img alt="" loading="lazy" src={`/api/media/${encodeURIComponent(c.mediaAssetId)}/thumb`} onError={(e) => (e.currentTarget.style.visibility = 'hidden')} />
                <span className="play" aria-hidden="true">
                  ▶
                </span>
                {c.edit && <span className="sv-thumb-tag">✂</span>}
              </button>
            ) : (
              <div className="sv-thumb none">{c.mediaCode ?? '—'}</div>
            )
          ) : (
            <div className="sv-thumb cg">
              <CgPreview template={c.template} lines={c.lines} />
            </div>
          )}
          <div className="sv-cue-body">
            <div className="sv-cue-top">
              <span className={`sv-kind ${c.kind === 'video' ? 'video' : 'cg'}`}>{c.kind === 'video' ? t('svKindVideo') : t('svKindCg')}</span>
              <span className="sv-cue-dur">{c.kind === 'video' ? (c.media ? secs(c.media.durationMs) : '') : c.durationMs ? `${Math.round(c.durationMs / 1000)}″` : ''}</span>
            </div>
            <div className="sv-cue-title">
              {c.kind === 'video' ? (c.edit ? `✂ ${c.edit.title}` : (c.media?.title ?? c.mediaCode ?? t('storyVideoNone'))) : (lines[0] ?? templateName(c.template, locale))}
            </div>
            <div className="sv-cue-meta">
              {c.kind === 'video' ? (c.media ? `${c.media.filename} · ${c.media.status}` : c.mediaCode ? t('virtualWaiting') : '') : lines.length ? [templateName(c.template, locale), ...lines.slice(1)].join(' · ') : '—'}
            </div>
            <div className={`sv-cue-anchor${c.anchor ? '' : ' manual'}`} title={t('svAnchor')}>
              ↳ {c.label}
              {!c.anchor && <span> · {t('cueManual')}</span>}
            </div>
          </div>
        </li>
        );
      })}
    </ol>
  );
  const fact = (label: string, value: ReactNode) => (
    <div className="sv-fact">
      <span>{label}</span>
      <b>{value || '—'}</b>
    </div>
  );
  return (
    <StrapDesignContext.Provider value={strapDesignFor(place?.programmeName).key}>
    <div className="sv">
      <div className="sv-bar">
        <div className="sv-places">
          {data.placements.map((p) => (
            <Link key={p.itemId} className="sv-place" to={`/rundown/${p.rundownId}?item=${p.itemId}`} title={t('bulletin')}>
              <b>{p.programmeName}</b>
              <span>{fmtDateInput(p.date)}</span>
              <span className="sv-place-pos" title={t('order')}>
                #{p.position + 1}
              </span>
            </Link>
          ))}
          {!data.placements.length && <span className="sv-place none">{t('svNoBulletin')}</span>}
        </div>
        {actions}
      </div>

      <header className="sv-head">
        <div className="sv-head-main">
          <div className="sv-eyebrow">
            <span>{t('storyView')}</span>
            {story.displayNumber && story.displayNumber.trim() !== '-' && (
              <span className="sv-number" title={t('storyNumber')}>
                {t('storyNumber')} {story.displayNumber}
              </span>
            )}
            <StoryStatusBadge status={story.status} />
          </div>
          <h1 className="sv-title">{story.title}</h1>
          <div className="sv-facts">
            {fact(t('origin'), data.originName)}
            {fact(t('category'), story.categoryId ? categories[story.categoryId] : null)}
            {fact(t('updatedAt'), `${fmtDateTime(story.updatedAt)}${data.updatedBy ? ` · ${data.updatedBy}` : ''}`)}
          </div>
        </div>
        <div className="sv-times">
          <div className="sv-time" title={story.readOverrideS !== null ? `${t('computed')} ${mmss(story.readComputedS)}` : undefined}>
            <span>{t('colRead')}</span>
            <b>{mmss(readS)}</b>
            {story.readOverrideS !== null && (
              <i>
                ✎ {t('computed')} {mmss(story.readComputedS)}
              </i>
            )}
          </div>
          <div className="sv-time" title={t('svExpected')}>
            <span>{t('colVideo')}</span>
            <b>{mmss(story.videoS)}</b>
          </div>
          <div className="sv-time total">
            <span>{t('colTotal')}</span>
            <b>{mmss(readS + story.videoS)}</b>
          </div>
        </div>
      </header>

      <div className="sv-body">
        <section className="sv-panel sv-script">
          <h2 className="sv-h">
            {t('text')}
            <span>
              {story.wordCount} {t('words')} · {mmss(story.readComputedS)}
            </span>
          </h2>
          {story.guideComments && (
            <div className="sv-note">
              <b>{t('guideComments')}</b>
              {story.guideComments}
            </div>
          )}
          <div className="sv-text" dangerouslySetInnerHTML={{ __html: sanitize(story.bodyHtml) }} />
        </section>

        <aside className="sv-side">
          <section className="sv-panel sv-video">
            <h2 className="sv-h">{t('storyVideo')}</h2>
            {videoPanel}
          </section>
          <section className="sv-panel">
            <h2 className="sv-h">
              {t('storyboard')}
              {cues && cues.length > 0 && <span className="sv-count">{cues.length}</span>}
            </h2>
            {board}
          </section>
          <section className="sv-panel">
            <h2 className="sv-h">{t('svRecord')}</h2>
            <dl className="sv-record">
              <dt>{t('storyNumber')}</dt>
              <dd>{story.displayNumber ?? '—'}</dd>
              <dt>{t('order')}</dt>
              <dd>{first ? first.position + 1 : '—'}</dd>
              <dt>{t('bulletinDate')}</dt>
              <dd>{first ? fmtDateInput(first.date) : '—'}</dd>
              <dt>{t('createdAt')}</dt>
              <dd>
                {fmtDateTime(story.createdAt)}
                {data.createdBy && <span className="muted"> · {data.createdBy}</span>}
              </dd>
              <dt>{t('updatedAt')}</dt>
              <dd>
                {fmtDateTime(story.updatedAt)}
                {data.updatedBy && <span className="muted"> · {data.updatedBy}</span>}
              </dd>
              <dt>{t('revision')}</dt>
              <dd>{story.revision}</dd>
            </dl>
          </section>
        </aside>
      </div>

      {preview?.mediaAssetId && <PreviewDialog mediaAssetId={preview.mediaAssetId} title={preview.media?.title ?? preview.label} meta={preview.media ? `${preview.media.filename} · ${secs(preview.media.durationMs)}` : ''} edit={preview.edit} cut={preview} onClose={() => setPreview(null)} />}
      {history && (
        <Dialog title={`${t('history')} — ${story.title}`} onClose={() => setHistory(null)} width={900}>
          <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 10 }}>
            <table className="grid">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Ημ/νία</th>
                  <th>Χρήστης</th>
                  <th>Ενέργεια</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {history.map((r) => (
                  <tr key={r.revision} onClick={() => setSelectedRev(r)} className={selectedRev?.revision === r.revision ? 'cursor' : ''} style={{ cursor: 'pointer' }}>
                    <td className="num">{r.revision}</td>
                    <td>{fmtDateTime(r.savedAt)}</td>
                    <td>{r.savedBy ?? ''}</td>
                    <td className="c">{r.action}</td>
                    <td className="muted">{r.clientIp ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div>
              {selectedRev && (
                <>
                  <div className="toolbar left">
                    <b>{selectedRev.title}</b>
                    {data.canEdit && selectedRev.revision !== story.revision && (
                      <button className="btn" onClick={() => void restore(selectedRev.revision)}>
                        ↺ {t('restore')}
                      </button>
                    )}
                  </div>
                  <pre className="preview" style={{ whiteSpace: 'pre-wrap', maxHeight: 420 }}>
                    {selectedRev.bodyText}
                  </pre>
                </>
              )}
            </div>
          </div>
        </Dialog>
      )}
    </div>
    </StrapDesignContext.Provider>
  );
}

/** Keep the editor's own tags; drop scripts, handlers and foreign tags. */
export function sanitize(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const allowed = new Set(['P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'UL', 'OL', 'LI', 'H1', 'H2', 'H3', 'BLOCKQUOTE', 'SPAN', 'DIV']);
  const walk = (node: Element) => {
    for (const child of [...node.children]) {
      if (!allowed.has(child.tagName)) {
        child.replaceWith(...child.childNodes);
        continue;
      }
      for (const attr of [...child.attributes]) child.removeAttribute(attr.name);
      walk(child);
    }
  };
  walk(doc.body);
  return doc.body.innerHTML;
}
